/**
 * Crane change-password complexity rules. Better Auth only exposes a min length; these
 * requirements are enforced in `passwordPolicy.ts` (client checklist), `server/lib/auth.ts`
 * (`/change-password` and OTP reset hooks), and signup / reset forms.
 *
 * **`CHANGE_PASSWORD_REQUIREMENTS` is the registry** — add, remove, or reorder rows to
 * change policy; `isValidChangePassword`, API error copy, and the profile checklist
 * all derive from it. Tune minimum length via `CHANGE_PASSWORD_MIN_LENGTH`.
 */
export const CHANGE_PASSWORD_MIN_LENGTH = 8

export type ChangePasswordRequirement = {
    id: string
    /** Live checklist row on the change-password form. */
    label: string
    test: (password: string) => boolean
}

export function hasMinPasswordLength(password: string): boolean {
    return password.length >= CHANGE_PASSWORD_MIN_LENGTH
}

export function hasUpperAndLowerCase(password: string): boolean {
    return /[a-z]/.test(password) && /[A-Z]/.test(password)
}

export function hasNumber(password: string): boolean {
    return /\d/.test(password)
}

/** Any character that is not a letter, digit, or whitespace. */
export function hasSpecialCharacter(password: string): boolean {
    return /[^a-zA-Z0-9\s]/.test(password)
}

export function hasNoWhitespace(password: string): boolean {
    return !/\s/.test(password)
}

/** Ordered rules for UI checklist + validation (single source of truth). */
export const CHANGE_PASSWORD_REQUIREMENTS: readonly ChangePasswordRequirement[] = [
    {
        id: 'minLength',
        label: `At least ${CHANGE_PASSWORD_MIN_LENGTH} characters`,
        test: hasMinPasswordLength,
    },
    {
        id: 'upperAndLower',
        label: 'An uppercase and a lowercase letter',
        test: hasUpperAndLowerCase,
    },
    {
        id: 'number',
        label: 'A number',
        test: hasNumber,
    },
    {
        id: 'specialNoSpace',
        label: 'A special character, no spaces',
        test: (password) => hasSpecialCharacter(password) && hasNoWhitespace(password),
    },
]

export type ChangePasswordRequirementResult = {
    id: string
    label: string
    met: boolean
}

/** Live checklist rows for a password value. */
export function evaluateChangePasswordRequirements(
    password: string
): ChangePasswordRequirementResult[] {
    return CHANGE_PASSWORD_REQUIREMENTS.map(({ id, label, test }) => ({
        id,
        label,
        met: test(password),
    }))
}

export function isValidChangePassword(password: string): boolean {
    return CHANGE_PASSWORD_REQUIREMENTS.every((requirement) => requirement.test(password))
}

/**
 * Alert title when complexity fails. Pair with `CHANGE_PASSWORD_REQUIREMENTS_MESSAGE`
 * via `getAuthErrorConfig` / `splitAuthErrorMessage` (bold header + regular body).
 */
export const CHANGE_PASSWORD_REQUIREMENTS_TITLE = 'Password is not valid'

/** Server / auth error body when complexity fails (`auth.ts` before hook). */
export const CHANGE_PASSWORD_REQUIREMENTS_MESSAGE =
    'It must be at least 8 characters long, include at least one uppercase letter, one lowercase letter, one number, and one special character, and contain no spaces.'

/** Intro line under the change-password title (profile form). */
export function changePasswordIntroCopy(): string {
    return `Choose a new password for your Crane account. Use at least ${CHANGE_PASSWORD_MIN_LENGTH} characters with a mix of letters, numbers, and a special character.`
}
